// src/GenericTablePage.js
import React, { useEffect, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  InputAdornment,
  IconButton,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import GetAppIcon from '@mui/icons-material/GetApp';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import * as XLSX from 'xlsx';
import { supabase } from './supabaseClient';
import navItems from './navItems';
import ProfileDialog from './ProfileDialog';

export default function GenericTablePage() {
  const location = useLocation();
  const navItem   = navItems.find(item => item.path === location.pathname);
  const tableName = navItem?.table;
  const title     = navItem?.label || tableName;

  const [rows, setRows]               = useState([]);
  const [columns, setColumns]         = useState([]);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState(null);
  const [filter, setFilter]           = useState('');
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue]     = useState('');
  const [saving, setSaving]           = useState(false);
  const [dialogPhone, setDialogPhone] = useState(null);

  const fetchData = useCallback(async () => {
    if (!tableName) return;
    setLoading(true);
    setError(null);
    
    try {
      const { data, error: queryError } = await supabase
        .from(tableName)
        .select('*');
      
      setLoading(false);
      
      if (queryError) {
        console.error('Error fetching data:', queryError);
        setError(`Error: ${queryError.message}`);
        setRows([]);
        setColumns([]);
      } else {
        setRows(data || []);
        if (data && data.length > 0) {
          setColumns(Object.keys(data[0]));
        } else {
          setColumns([]);
        }
      }
    } catch (err) {
      setLoading(false);
      console.error('Error fetching data:', err);
      setError(`Error: ${err.message || 'Failed to fetch data'}`);
      setRows([]);
      setColumns([]);
    }
  }, [tableName]);

  useEffect(() => {
    fetchData();
  }, [tableName, fetchData]);

  const openProfile  = phone => setDialogPhone(phone);
  const closeProfile = ()    => setDialogPhone(null);

  const exportToExcel = () => {
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(rows);
    XLSX.utils.book_append_sheet(wb, ws, tableName);
    XLSX.writeFile(wb, `${tableName}.xlsx`);
  };

  const filteredRows = rows.filter(row =>
    filter === '' ||
    Object.values(row).some(v =>
      v != null && v.toString().toLowerCase().includes(filter.toLowerCase())
    )
  );

  const startEdit = (idx, col, val) => {
    // don’t inline-edit any of the phone fields
    if (col === 'phone_number' ||
        col === 'sender_phone_number' ||
        col === 'receiver_phone_number') return;
    setEditingCell({ idx, col });
    setEditValue(val == null ? '' : String(val));
  };
  const cancelEdit = () => {
    setEditingCell(null);
    setEditValue('');
  };
  const commitEdit = async () => {
    if (!editingCell) return;
    const { idx, col } = editingCell;
    const row = rows[idx];
    if (row.id == null) {
      alert('Cannot update a row without an id');
      cancelEdit();
      return;
    }
    setSaving(true);
    const { error } = await supabase
      .from(tableName)
      .update({ [col]: editValue })
      .eq('id', row.id);
    setSaving(false);
    if (error) {
      alert(`Update failed: ${error.message}`);
    } else {
      const updated = [...rows];
      updated[idx][col] = editValue;
      setRows(updated);
    }
    cancelEdit();
  };

  const isImageUrl = u =>
    typeof u === 'string' && /\.(jpe?g|png|gif|svg)$/i.test(u);

  // any of these field names become a profile link:
  const isPhoneField = col =>
    col === 'phone_number' ||
    col === 'sender_phone_number' ||
    col === 'receiver_phone_number';

  return (
    <Box sx={{ p: 2, overflowX: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">{title}</Typography>
        <Button onClick={fetchData} disabled={loading}>
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <TextField
          placeholder="Search…"
          size="small"
          value={filter}
          onChange={e => setFilter(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ mr: 2, width: 240 }}
        />
        <Button
          startIcon={<GetAppIcon />}
          onClick={exportToExcel}
          variant="outlined"
          sx={{ mr: 2 }}
        >
          Export
        </Button>
        {saving && <CircularProgress size={24} />}
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper sx={{ width: '100%', overflow: 'auto' }}>
          <TableContainer>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {columns.map(col => (
                    <TableCell key={col} sx={{ whiteSpace: 'nowrap' }}>
                      {col
                        .replace(/_/g, ' ')
                        .replace(/\b\w/g, c => c.toUpperCase())}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRows.map((row, idx) => (
                  <TableRow key={row.id ?? idx} hover>
                    {columns.map(col => {
                      const val = row[col];
                      const isEditing =
                        editingCell?.idx === idx &&
                        editingCell.col === col;

                      // phone fields open profile dialog
                      if (isPhoneField(col)) {
                        return (
                          <TableCell key={col}>
                            <Typography
                              component="span"
                              sx={{
                                color: 'primary.main',
                                cursor: 'pointer',
                                textDecoration: 'underline',
                              }}
                              onClick={() => openProfile(val)}
                            >
                              {val}
                            </Typography>
                          </TableCell>
                        );
                      }

                      // render image URLs inline
                      if (isImageUrl(val)) {
                        return (
                          <TableCell key={col}>
                            <Box
                              component="img"
                              src={val}
                              alt=""
                              sx={{ maxHeight: 50, maxWidth: 100 }}
                            />
                          </TableCell>
                        );
                      }

                      // everything else (inline edits)
                      return (
                        <TableCell
                          key={col}
                          onDoubleClick={() => startEdit(idx, col, val)}
                          sx={{
                            maxWidth: 200,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            cursor: isPhoneField(col)
                              ? 'default'
                              : 'pointer',
                          }}
                        >
                          {isEditing ? (
                            <Box
                              sx={{ display: 'flex', alignItems: 'center' }}
                            >
                              <TextField
                                value={editValue}
                                onChange={e => setEditValue(e.target.value)}
                                size="small"
                                onBlur={commitEdit}
                                onKeyDown={e => {
                                  if (e.key === 'Enter')
                                    commitEdit();
                                  if (e.key === 'Escape')
                                    cancelEdit();
                                }}
                                autoFocus
                                fullWidth
                              />
                              <IconButton
                                size="small"
                                onMouseDown={e => e.preventDefault()}
                                onClick={commitEdit}
                              >
                                <CheckIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onMouseDown={e => e.preventDefault()}
                                onClick={cancelEdit}
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          ) : (
                            val != null ? String(val) : ''
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <ProfileDialog
        open={Boolean(dialogPhone)}
        phoneNumber={dialogPhone}
        onClose={closeProfile}
      />
    </Box>
  );
}
